export interface UserAccessibility {
  isAccessibleForUser: boolean;
  membershipRole?: string;
}

export interface UserAccessibilityCheckParams {
  userAccessibility?: UserAccessibility;
  isAuthenticated: boolean;
  isMembershipActive: boolean;
}

/**
 * Checks if a user has access to submit answers based on userAccessibility settings.
 *
 * Logic:
 * - If isAccessibleForUser === true → everyone can access (no login required)
 * - If isAccessibleForUser === false → check membershipRole:
 *   - visitor: everyone can submit (should be together with isAccessibleForUser === true)
 *   - customer: must be at least logged in to submit
 *   - member: must be logged in and active member to submit
 *
 * @param params - Object containing userAccessibility, isAuthenticated, and isMembershipActive
 * @returns true if user has access, false otherwise
 */
export const checkUserAccess = ({
  userAccessibility,
  isAuthenticated,
  isMembershipActive,
}: UserAccessibilityCheckParams): boolean => {
  const isAccessibleForUser = userAccessibility?.isAccessibleForUser ?? true;
  const membershipRole = userAccessibility?.membershipRole;

  // If isAccessibleForUser === true, everyone can access (no login required)
  if (isAccessibleForUser === true) {
    return true;
  }

  // If isAccessibleForUser === false, check membershipRole (case-insensitive)
  if (isAccessibleForUser === false) {
    const normalizedRole = membershipRole?.toLowerCase();

    // visitor: everyone can submit
    if (normalizedRole === 'visitor') {
      return true;
    }

    // customer: must be at least logged in
    if (normalizedRole === 'customer') {
      return isAuthenticated;
    }

    // member: must be logged in and active member
    if (normalizedRole === 'member') {
      return isAuthenticated && isMembershipActive;
    }

    // If no membershipRole specified and isAccessibleForUser is false, deny access
    return false;
  }

  // Default: allow access if isAccessibleForUser is true (default behavior)
  return true;
};
